/**
 * Every platform endpoint this panel calls, written once.
 *
 * Paths are relative to `/api/platform/v1` (see `API_ROOT` in `lib/api.ts`) and
 * use the controller's own `:param` spelling, so `routes.spec.ts` can read
 * `src/modules/**\/*.controller.ts` and check each entry against a real
 * decorator. That is the check the type system cannot make: a wrong path
 * compiles, builds, and 404s at runtime — which is how four screens of the
 * previous panel shipped dead.
 *
 * Nothing outside `lib/platform` and `lib/upload.ts` reads this table. Pages
 * call the functions in `client.ts`.
 */

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RouteDef {
  readonly method: HttpMethod;
  readonly path: `/${string}`;
}

const route = <M extends HttpMethod, P extends `/${string}`>(method: M, path: P) => ({ method, path }) as const;

export const ROUTES = {
  signIn: route('POST', '/auth/login'),
  refresh: route('POST', '/auth/refresh'),
  signOut: route('POST', '/auth/logout'),
  me: route('GET', '/me'),

  overview: route('GET', '/overview'),
  insights: route('GET', '/insights'),
  permissions: route('GET', '/permissions'),
  models: route('GET', '/models'),
  modelTenants: route('GET', '/models/:model/tenants'),
  verticals: route('GET', '/verticals'),
  defaultBranding: route('GET', '/email-branding'),
  setDefaultBranding: route('PUT', '/email-branding'),
  audit: route('GET', '/audit'),
  auditActions: route('GET', '/audit/actions'),
  auditIntegrity: route('GET', '/audit/integrity'),

  tenants: route('GET', '/tenants'),
  slugCheck: route('GET', '/tenants/slug-check'),
  createTenant: route('POST', '/tenants'),
  tenant: route('GET', '/tenants/:id'),
  updateTenant: route('PATCH', '/tenants/:id'),
  tenantOverview: route('GET', '/tenants/:id/overview'),
  setFrontendUrl: route('PUT', '/tenants/:id/frontend-url'),
  setLogo: route('PUT', '/tenants/:id/logo'),
  tenantAudit: route('GET', '/tenants/:id/audit'),
  tenantAuditActions: route('GET', '/tenants/:id/audit/actions'),
  tenantAuditIntegrity: route('GET', '/tenants/:id/audit/integrity'),
  tenantActivity: route('GET', '/tenants/:id/activity'),
  tenantContentSummary: route('GET', '/tenants/:id/content-summary'),
  tenantNotificationsSummary: route('GET', '/tenants/:id/notifications-summary'),

  members: route('GET', '/tenants/:id/users'),
  createMember: route('POST', '/tenants/:id/users'),
  updateMember: route('PATCH', '/tenants/:id/users/:userId'),
  inviteMember: route('POST', '/tenants/:id/users/:userId/invitation'),
  restoreMember: route('POST', '/tenants/:id/users/:userId/restore'),
  memberPermissions: route('GET', '/tenants/:id/users/:userId/permissions'),
  grantPermission: route('PUT', '/tenants/:id/users/:userId/permissions/:permission'),
  revokePermission: route('DELETE', '/tenants/:id/users/:userId/permissions/:permission'),

  joinRequests: route('GET', '/tenants/:id/join-requests'),
  approveJoin: route('POST', '/tenants/:id/join-requests/:userId/approve'),
  rejectJoin: route('POST', '/tenants/:id/join-requests/:userId/reject'),

  roles: route('GET', '/tenants/:id/roles'),
  setRolePermissions: route('PUT', '/tenants/:id/roles/:roleId/permissions'),

  mail: route('GET', '/tenants/:id/mail'),
  setMail: route('PUT', '/tenants/:id/mail'),
  testMail: route('POST', '/tenants/:id/mail/test'),
  mailDispatches: route('GET', '/tenants/:id/mail/dispatches'),

  emailTemplates: route('GET', '/tenants/:id/email-templates'),
  setEmailTemplate: route('PUT', '/tenants/:id/email-templates/:key'),
  testEmailTemplate: route('POST', '/tenants/:id/email-templates/:key/test'),
  previewEmailTemplate: route('POST', '/tenants/:id/email-templates/:key/preview'),
  emailBranding: route('GET', '/tenants/:id/email-branding'),
  setEmailBranding: route('PUT', '/tenants/:id/email-branding'),

  identityProvider: route('GET', '/tenants/:id/identity-provider'),
  setIdentityProvider: route('PUT', '/tenants/:id/identity-provider'),
  signInRegistration: route('GET', '/tenants/:id/identity-provider/registration'),
  identityProviderDirectory: route('GET', '/tenants/:id/identity-provider/directory'),

  tenantModels: route('GET', '/tenants/:id/models'),
  setTenantModel: route('PUT', '/tenants/:id/models/:model'),
  setTenantModelPricing: route('PUT', '/tenants/:id/models/:model/pricing'),
  modelPriceList: route('GET', '/models/price-list'),
  modelRateCard: route('GET', '/model-prices'),
  recordModelPrice: route('PUT', '/model-prices/:model'),
  setModelOffering: route('PUT', '/model-prices/:model/offering'),
  fxRate: route('GET', '/fx-rate'),
  setFxRate: route('PUT', '/fx-rate'),
  restoreTests: route('GET', '/restore-tests'),
  recordRestoreTest: route('POST', '/restore-tests'),

  presignFiles: route('POST', '/tenants/:id/files/presign'),
  fileParts: route('POST', '/tenants/:id/files/:fileId/parts'),
  completeFile: route('POST', '/tenants/:id/files/:fileId/complete'),
  abortFile: route('DELETE', '/tenants/:id/files/:fileId'),
} as const satisfies Record<string, RouteDef>;

export type RouteName = keyof typeof ROUTES;

/**
 * Routes a design brief specifies that no controller declares yet.
 *
 * `routes.spec.ts` lets these be absent from the server — and fails the moment
 * one appears, so whoever sees it reads the real code, corrects the type that
 * was written from prose, and deletes the name from this list. Empty today:
 * the five §3 routes landed on 2026-10-07 and their types were corrected
 * against the backend (five differences from the brief; see `types.ts`).
 */
export const BRIEF_ROUTES: readonly RouteName[] = [];

type ParamNames<P extends string> = P extends `${string}:${infer Name}/${infer Rest}`
  ? Name | ParamNames<`/${Rest}`>
  : P extends `${string}:${infer Name}`
    ? Name
    : never;

export type RouteParams<R extends RouteDef> = [ParamNames<R['path']>] extends [never]
  ? Record<string, never>
  : Record<ParamNames<R['path']>, string>;

/**
 * A route's path with its parameters filled, each one percent-encoded.
 *
 * Encoded because two of them are not UUIDs: a template `:key` and a
 * permission code are strings the server chose, and a `/` or `?` in one would
 * otherwise address a different route — the kind of bug that answers 404 and
 * reads like a server fault.
 */
export function pathOf<R extends RouteDef>(definition: R, params: RouteParams<R>): string {
  return definition.path.replace(/:(\w+)/g, (_, name: string) => {
    const value = (params as Record<string, string | undefined>)[name];

    if (value === undefined || value === '') {
      throw new Error(`pathOf(${definition.path}): missing :${name}`);
    }

    return encodeURIComponent(value);
  });
}
